import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, Popconfirm, Typography, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { geoApi, District, Street } from '../../api/geo';

const { Title } = Typography;

const StreetsList: React.FC = () => {
  const [streets, setStreets] = useState<Street[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [districtFilter, setDistrictFilter] = useState<number | undefined>();
  const [modal, setModal] = useState<{ mode: 'create' | 'edit'; item?: Street } | null>(null);
  const [form] = Form.useForm();

  const fetch = async () => {
    setLoading(true);
    try {
      const [streetsRes, districtsRes] = await Promise.all([
        geoApi.listStreets({ search: search || undefined, district_id: districtFilter, limit: 200 }),
        geoApi.listDistricts(),
      ]);
      setStreets(streetsRes.data.items);
      setTotal(streetsRes.data.total);
      setDistricts(districtsRes.data);
    } catch { message.error('Ошибка загрузки'); }
    setLoading(false);
  };

  useEffect(() => { fetch(); }, [search, districtFilter]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    try {
      if (modal?.mode === 'edit' && modal.item) {
        await geoApi.updateStreet(modal.item.id, values);
        message.success('Улица обновлена');
      } else {
        await geoApi.createStreet(values);
        message.success('Улица добавлена');
      }
      setModal(null);
      form.resetFields();
      fetch();
    } catch { message.error('Ошибка сохранения'); }
  };

  const handleDelete = async (id: number) => {
    try {
      await geoApi.deleteStreet(id);
      message.success('Улица удалена');
      fetch();
    } catch { message.error('Ошибка удаления'); }
  };

  const districtName = (id: number | null) => {
    if (!id) return '—';
    return districts.find(d => d.id === id)?.name_ru || '—';
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: 'Название', dataIndex: 'name_ru' },
    { title: 'Район', dataIndex: 'district_id', render: (v: number | null) => districtName(v) },
    { title: 'Источник', dataIndex: 'source', width: 80 },
    {
      title: 'Действия', width: 120,
      render: (_: any, record: Street) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => {
            form.setFieldsValue(record);
            setModal({ mode: 'edit', item: record });
          }} />
          <Popconfirm title="Удалить улицу?" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Улицы ({total})</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => {
          form.resetFields();
          setModal({ mode: 'create' });
        }}>Добавить</Button>
      </div>

      <Space style={{ marginBottom: 16 }}>
        <Input prefix={<SearchOutlined />} placeholder="Поиск по названию"
          value={search} onChange={e => setSearch(e.target.value)} allowClear style={{ width: 250 }} />
        <Select placeholder="Все районы" allowClear style={{ width: 200 }}
          value={districtFilter} onChange={setDistrictFilter}>
          {districts.map(d => <Select.Option key={d.id} value={d.id}>{d.name_ru}</Select.Option>)}
        </Select>
      </Space>

      <Table columns={columns} dataSource={streets} rowKey="id" loading={loading}
        pagination={{ total, pageSize: 200 }} size="middle" />

      <Modal
        open={!!modal}
        title={modal?.mode === 'edit' ? 'Редактировать улицу' : 'Новая улица'}
        onCancel={() => setModal(null)}
        onOk={handleSubmit}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name_ru" label="Название (рус)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name_kk" label="Название (каз)">
            <Input />
          </Form.Item>
          <Form.Item name="district_id" label="Район">
            <Select placeholder="Выберите район" allowClear>
              {districts.map(d => <Select.Option key={d.id} value={d.id}>{d.name_ru}</Select.Option>)}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StreetsList;
